import type { ReactElement } from "react";
import { FaYoutube } from "react-icons/fa";
import { FiExternalLink } from "react-icons/fi";
import { ContentOptionsMenu } from "./ContentOptionsMenu";
import { CopyMarkdownButton } from "./CopyMarkdownButton";
import { ShareButton } from "./ShareButton";

interface Tag {
	id: string;
	name: string;
}

interface ContentHeaderProps {
	title: string | null;
	thumbnailUrl: string | null;
	sourceUrl: string;
	author?: string | null;
	publishedAt?: string | null;
	markdown?: string | null;
	tags?: Tag[];
	libraryItemId?: string;
	shareToken?: string | null;
	contentId?: string;
	onRemove?: () => void;
	canToggle?: boolean;
	showRaw?: boolean;
	onToggleRaw?: () => void;
}

function isYouTubeUrl(url: string): boolean {
	try {
		return new URL(url).hostname.includes("youtube");
	} catch {
		return false;
	}
}

function getSourceName(
	url: string,
	isYouTube: boolean,
	author?: string | null,
): string {
	if (isYouTube) return author ?? "YouTube";
	try {
		return new URL(url).hostname.replace("www.", "");
	} catch {
		return "Source";
	}
}

export function ContentHeader({
	title,
	thumbnailUrl,
	sourceUrl,
	author,
	publishedAt,
	markdown,
	tags,
	libraryItemId,
	shareToken,
	contentId,
	onRemove,
	canToggle,
	showRaw,
	onToggleRaw,
}: ContentHeaderProps): ReactElement {
	const isYouTube = isYouTubeUrl(sourceUrl);
	const sourceName = getSourceName(sourceUrl, isYouTube, author);

	const hiResThumbnail = thumbnailUrl?.replace(
		/\/(default|sddefault|hqdefault|mqdefault)\./,
		"/maxresdefault.",
	);

	return (
		<section className="mb-6">
			<div className="mb-1 flex items-start justify-between gap-4">
				<h1
					data-testid="content-title"
					className="text-fg-strong text-xl leading-snug font-semibold"
				>
					{title ?? sourceUrl}
				</h1>
				<div className="flex shrink-0 items-center gap-2">
					<CopyMarkdownButton markdown={markdown} />
					{libraryItemId && (
						<ShareButton
							libraryItemId={libraryItemId}
							shareToken={shareToken ?? null}
							contentId={contentId}
						/>
					)}
					<a
						href={sourceUrl}
						target="_blank"
						rel="noopener noreferrer"
						className="border-line text-fg-faint hover:border-accent-muted hover:text-accent flex items-center gap-1.5 rounded-full border px-3 py-1 text-[10px] tracking-wider uppercase transition-colors"
					>
						Open
						<FiExternalLink className="text-[10px]" />
					</a>
					<ContentOptionsMenu
						sourceUrl={sourceUrl}
						contentTitle={title}
						markdown={markdown}
						onRemove={onRemove}
						canToggle={canToggle}
						showRaw={showRaw}
						onToggleRaw={onToggleRaw}
					/>
				</div>
			</div>

			<div className="text-fg-tertiary flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
				<span
					data-testid="content-source"
					className="flex items-center gap-1"
				>
					{isYouTube && <FaYoutube className="text-sm" />}
					{sourceName}
				</span>
				{publishedAt && (
					<>
						<span className="text-fg-ghost">&middot;</span>
						<span data-testid="published-date">
							{new Date(publishedAt).toLocaleDateString("en-US", {
								month: "short",
								day: "numeric",
								year: "numeric",
							})}
						</span>
					</>
				)}
				{tags && tags.length > 0 && (
					<>
						<span className="text-fg-ghost">&middot;</span>
						{tags.map((tag) => (
							<span key={tag.id} className="tag-pill">
								{tag.name}
							</span>
						))}
					</>
				)}
			</div>

			{hiResThumbnail && (
				<a
					href={sourceUrl}
					target="_blank"
					rel="noopener noreferrer"
					className="border-line hover:border-line-emphasis mt-4 block aspect-video w-full overflow-hidden rounded-lg border bg-gray-100 transition-colors md:w-[340px]"
				>
					<img
						src={hiResThumbnail}
						alt=""
						className="h-full w-full object-cover opacity-80 transition-opacity duration-150 hover:opacity-100"
					/>
				</a>
			)}
		</section>
	);
}
